"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.explodeUnits = explodeUnits;
exports.applyPromotions = applyPromotions;
const errors_1 = require("../errors");
const actions_1 = require("./actions");
const code_1 = require("./code");
const least_expensive_1 = require("./least-expensive");
// La v1 duplique chaque ligne par sa quantité pour sélectionner les articles un
// par un (prepareOrderItems, promotion.ts:695-706). `unitPrice` est le prix de
// base, options et suppléments exclus (getPriceForItem, promotion.ts:678-686).
function explodeUnits(items) {
    return items.flatMap((item, itemIndex) => Array.from({ length: item.quantity }, () => ({
        itemIndex,
        referenceId: item.referenceId,
        kind: item.kind,
        unitPrice: item.unitPrice,
    })));
}
// Ordre de sortie de la v1 (getAllPromotionsTypesComputed, promotion.ts:143-169) :
// LEAST_EXPENSIVE, CODE, puis ACTIONS par priorité décroissante. Les trois
// familles se calculent sur le même sous-total d'origine.
function applyPromotions(items, promotions, mode) {
    const subtotal = items.reduce((sum, item) => sum + item.subtotal, 0);
    const leastExpensive = [];
    const codes = [];
    const actions = [];
    for (const promotion of promotions) {
        switch (promotion.family) {
            case 'LEAST_EXPENSIVE':
                leastExpensive.push(promotion);
                break;
            case 'CODE':
                codes.push(promotion);
                break;
            case 'ACTIONS':
                actions.push(promotion);
                break;
        }
    }
    const units = explodeUnits(items);
    const computed = [
        ...(0, least_expensive_1.computeLeastExpensivePromotions)(units, leastExpensive),
        ...(0, code_1.computeCodePromotions)(subtotal, codes),
        ...(0, actions_1.computeActionsPromotions)(units, actions, subtotal),
    ];
    // Cumul des remises article par ligne. Le referenceId identifie une ligne de
    // panier de façon unique (c'est l'identifiant de ligne de la v1) : une
    // collision est une erreur, jamais une remise silencieusement rognée.
    const indexByReference = new Map();
    items.forEach((item, index) => {
        if (indexByReference.has(item.referenceId)) {
            throw new errors_1.DuplicateReferenceError(`applyPromotions (mode ${mode}) : deux lignes portent la référence « ${item.referenceId} » — le ciblage des promotions serait ambigu`);
        }
        indexByReference.set(item.referenceId, index);
    });
    const perLine = items.map(() => 0);
    for (const promotion of computed) {
        for (const itemDiscount of promotion.itemsDiscounts) {
            const index = indexByReference.get(itemDiscount.referenceId);
            if (index === undefined) {
                throw new errors_1.UnknownReferenceError(`applyPromotions (mode ${mode}) : référence inconnue « ${itemDiscount.referenceId} »`);
            }
            perLine[index] = (perLine[index] ?? 0) + itemDiscount.discount;
        }
    }
    // Plafonnements (R3, invariant §3.3 : une promotion ne dépasse jamais ce
    // qu'elle cible). Par ligne d'abord — garde, jamais atteinte tant qu'une
    // remise unitaire reste bornée au prix unitaire —, puis les remises de
    // commande, séquentiellement, sur le reste à payer de la commande.
    const promotionDiscounts = items.map((item, index) => Math.min(perLine[index] ?? 0, item.subtotal));
    let remainingOrder = subtotal - promotionDiscounts.reduce((sum, amount) => sum + amount, 0);
    const cappedPromotions = computed.map((promotion) => {
        const orderDiscount = Math.min(promotion.orderDiscount, remainingOrder);
        remainingOrder -= orderDiscount;
        if (orderDiscount === promotion.orderDiscount)
            return promotion;
        const itemsTotal = promotion.itemsDiscounts.reduce((sum, discount) => sum + discount.discount, 0);
        return { ...promotion, orderDiscount, totalDiscount: orderDiscount + itemsTotal };
    });
    return {
        items: items.map((item, index) => ({ ...item, promotionDiscount: promotionDiscounts[index] ?? 0 })),
        promotions: cappedPromotions,
    };
}
